import React from 'react';
import { ResponsiveBar } from '@nivo/bar';

import { TIME_DURATION } from '../../constants';
import { ALL_MONTHS } from '../../staticData';
import CircularProgress from '@material-ui/core/CircularProgress';
import styles from './MultiBarGraph.module.css';
import { BoxLegendSvg } from '@nivo/legends';

const getXAxisLabel = (data, durationText) => {
  const { value } = data;
  let labelData = value.split(' ');

  if (durationText === TIME_DURATION.LAST_SEVEN_DAYS) {
    const date = labelData[0];
    const month = labelData[1];
    return renderXAxisLabel(data, date, month);
  } else if (durationText === TIME_DURATION.LAST_FOUR_WEEKS) {
    let datesInARange = labelData[0].split('-');
    if (parseInt(datesInARange[0]) < parseInt(datesInARange[1])) {
      return renderXAxisLabel(data, labelData[0], labelData[1]);
    } else {
      const month = labelData[1];
      const startDateLabel =
        datesInARange[0] + ' ' + getPreviousMothName(month) + ' -';
      const endDateLabel = datesInARange[1] + ' ' + month + '  ';
      return renderXAxisLabel(data, startDateLabel, endDateLabel);
    }
  }
};

const renderXAxisLabel = (data, firstLabel, secondLabel) => {
  const { opacity, textAnchor, textBaseline, textX, textY, x, y } = data;
  return (
    <g transform={`translate(${x + 2},${y + 4})`} style={{ opacity }}>
      <text
        alignmentBaseline={textBaseline}
        textAnchor={textAnchor}
        transform={`translate(${textX},${textY})`}
      >
        <tspan style={{ display: 'block', fontSize: '10px' }}>
          {firstLabel}
        </tspan>
      </text>
      <text
        alignmentBaseline={textBaseline}
        textAnchor={textAnchor}
        transform={`translate(${textX},${textY + 10})`}
      >
        <tspan style={{ display: 'block', fontSize: '10px' }}>
          {secondLabel}
        </tspan>
      </text>
    </g>
  );
};

const getPreviousMothName = month => {
  let counter = 0;
  for (; counter < ALL_MONTHS.length; counter++) {
    if (ALL_MONTHS[counter] === month) {
      break;
    }
  }
  if (counter === 0) {
    return ALL_MONTHS[ALL_MONTHS.length - 1];
  } else {
    return ALL_MONTHS[counter - 1];
  }
};

const getPadding = durationText => {
  if (durationText === TIME_DURATION.LAST_SEVEN_DAYS) {
    return 0.2;
  } else {
    return 0.5;
  }
};

const getColor = ({ id }) => {
  const colorMap = new Map();
  colorMap.set('Planned', '#7EC3A7');
  colorMap.set('RollOut', '#0380AF');
  colorMap.set('LineOn', '#608FC9');
  colorMap.set('LineOut', '#F17B7B');
  colorMap.set('Dropped', '#52A281');
  colorMap.set('WTCL', '#4A806A');
  colorMap.set('SFLT', '#006C40');

  return colorMap.get(id);
};

const MultiBarGraph = ({
  data,
  maxRecordCount,
  isLoading,
  durationText,
  keyFactors,
}) => {
  if (isLoading) {
    return (
      <div className={styles.graphLoaderIcon}>
        <CircularProgress />
      </div>
    );
  } else {
    return (
      <ResponsiveBar
        data={data}
        layers={[...['grid', 'axes', 'bars', 'markers'], BarLegend]}
        colors={getColor}
        keys={keyFactors}
        indexBy="id"
        margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
        minValue={0}
        maxValue={maxRecordCount}
        padding={getPadding(durationText)}
        groupMode="grouped"
        borderRadius={2}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 20,
          tickRotation: 0,
          renderTick: data => {
            return getXAxisLabel(data, durationText);
          },
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 20,
          tickRotation: 0,
          legend: 'count',
          legendPosition: 'middle',
          legendOffset: -50,
        }}
        legends={[
          {
            dataFrom: 'keys',
            data: keyFactors.map((id, index) => ({
              index,
              label: id,
              color: getColor({ id: id }),
            })),
            anchor: 'right',
            direction: 'column',
            justify: false,
            translateX: 160,
            translateY: 0,
            itemsSpacing: 2,
            itemWidth: 100,
            itemHeight: 20,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolSize: 15,
            effects: [
              {
                on: 'hover',
                style: {
                  itemOpacity: 1,
                },
              },
            ],
          },
        ]}
        enableLabel={true}
        labelTextColor="#000000"
        labelFormat={d => (
          <tspan y={-5} fontSize={8} fontWeight={600}>
            {d}
          </tspan>
        )}
        animate={true}
        motionStiffness={90}
        motionDamping={15}
        tooltip={({ id, value, color }) => (
          <div style={{ position: 'relative' }}>
            <div
              class="color-box"
              style={{
                backgroundColor: color,
                width: '10px',
                height: '10px',
                display: 'inline-block',
                marginRight: '5px',
              }}
            ></div>
            <div style={{ display: 'inline-block' }}>
              {id}: {value}
            </div>
          </div>
        )}
      />
    );
  }
};

export const BarLegend = ({ legends, height, width }) => {
  return legends.map((legend, index) => (
    <BoxLegendSvg
      key={`legend_${index}`}
      {...legend}
      containerHeight={height}
      containerWidth={width}
    />
  ));
};
export default MultiBarGraph;
